/**
 * Centralized cache invalidations for resources whose cache is shared across
 * features.
 *
 * Two families coexist here, and both belong:
 *  - the business resources (salarié, élu, ayant droit, contact externe,
 *    groupe), which appear in many places and share derived caches (audit logs,
 *    group membership, beneficiaries-by-employee...);
 *  - the aggregating resources, whose cache a *different* feature invalidates.
 *    `subsidy-policies` is one: /context_options aggregates the refunds, deals
 *    and yeati-loc catalogues, and the rule payload carries each context's name
 *    (resolved server-side at serialization time — nothing is denormalized in
 *    the database). Both responses therefore hold the label as it stood when
 *    they were fetched, so a rename in any of those features leaves the whole
 *    namespace stale, not just /context_options.
 *
 * Each resource has more than one query-key namespace in the codebase (lists,
 * infinite, allIds, filteredIds, detail variants...). Centralizing the
 * invalidation logic here avoids drift between mutations and missed
 * invalidations — and lets a feature refresh another one's cache without
 * importing its query keys, which the feature-isolation rule forbids.
 *
 * Usage:
 *   - In a mutation, declare `meta: { invalidateResources: ["employees"] }`
 *     and the global mutation handler in queryClient.ts will run the
 *     invalidations on success.
 *   - For ad-hoc usage (components, success callbacks of imperative jobs)
 *     call `invalidateCentralResources(queryClient, ["employees"])`.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";

export const CENTRAL_RESOURCES = [
  "employees",
  "representatives",
  "beneficiaries",
  "external-contacts",
  "groups",
  "audit-logs",
  "subsidy-policies",
] as const;

export type CentralResource = (typeof CENTRAL_RESOURCES)[number];

/**
 * For each central resource, list every distinct query-key namespace
 * (i.e. distinct cache root) that must be invalidated when the resource
 * is mutated. Each entry uses prefix matching (exact: false).
 *
 * Keep this list aligned with the per-feature queryKeys configs:
 *  - employees: src/features/employees/configs/queryKeys.ts
 *    (incl. "employee-dynamic-user-groups", also listed under groups)
 *  - representatives: src/features/representatives/configs/queryKeys.ts
 *    + src/shared/hooks/useRepresentativesData.ts (SHARED_REPRESENTATIVES_QUERY_KEYS)
 *  - beneficiaries: src/features/beneficiaries/configs/queryKeys.ts
 *  - external-contacts: src/features/external-contacts/configs/queryKeys.ts
 *  - groups: src/features/groups/configs/queryKeys.ts
 *    + src/shared/hooks/useGroupsData.ts (SHARED_GROUPS_QUERY_KEY)
 *    + src/features/dynamic-groups/hooks/useDynamicGroupPreview.ts ("dynamic-groups")
 *    + src/shared/hooks/useAffectedResources.ts ("affected-resources")
 *  - audit-logs: src/features/audit-logs/configs/queryKeys.ts
 *    + employee-/beneficiary-audit-logs (defined in their respective features)
 *  - subsidy-policies: src/features/subsidy-policies/configs/queryKeys.ts
 */
const RESOURCE_QUERY_KEYS: Record<CentralResource, readonly QueryKey[]> = {
  "employees": [
    ["employees"],
    ["allEmployeesIds"],
    ["filteredEmployeesIds"],
    ["employee-dynamic-user-groups"],
  ],
  "representatives": [
    ["representatives"],
    ["allRepresentativeIds"],
    ["shared-representatives"],
  ],
  "beneficiaries": [
    ["beneficiaries"],
    ["employee-beneficiaries"],
  ],
  "external-contacts": [
    ["externalContacts"],
    ["allExternalContactIds"],
    ["external-contact"],
  ],
  // ADM-856: a guarded resource mutation can transition referencing dynamic
  // groups (needs_review/suspended) and reset their member counts, so the
  // builder's preview reads are stale too. Listed here rather than duplicated in
  // each of the four features that mutate a guarded resource.
  "groups": [
    ["groups"],
    ["shared-groups"],
    ["all-user-groups"],
    ["user-groups-by-member"],
    ["dynamic-groups"],
    // A filter change, suspension or deletion changes which dynamic groups an
    // employee matches.
    ["employee-dynamic-user-groups"],
    // Only refreshed by a mutation that declares invalidateResources: ["groups"];
    // a de-affectation done from a business feature relies on staleTime instead.
    ["affected-resources"],
  ],
  "audit-logs": [
    ["auditLogs"],
    ["employee-audit-logs"],
    ["beneficiary-audit-logs"],
  ],
  "subsidy-policies": [
    ["subsidy-policies"],
  ],
};

/**
 * Invalidate every cache namespace associated with the given central resources.
 * Each query key is invalidated via prefix matching, and duplicates across
 * resources are deduplicated.
 *
 * Returns a promise that resolves once the active matching queries have
 * refetched, so callers can treat the refresh as part of a larger awaited
 * transaction (e.g. group-membership transfers).
 */
export const invalidateCentralResources = (
  queryClient: QueryClient,
  resources: readonly CentralResource[],
): Promise<void> => {
  const seen = new Set<string>();
  const refetches: Promise<void>[] = [];
  for (const resource of resources) {
    for (const queryKey of RESOURCE_QUERY_KEYS[resource]) {
      const signature = JSON.stringify(queryKey);
      if (seen.has(signature)) continue;
      seen.add(signature);
      refetches.push(queryClient.invalidateQueries({ queryKey, exact: false }));
    }
  }
  return Promise.all(refetches).then(() => undefined);
};

/**
 * Resources to invalidate when group membership changes (members added,
 * removed, or a member's groups are reassigned). Membership changes affect
 * the member's own caches (employees / representatives / external-contacts)
 * and the groups themselves.
 */
export const GROUP_MEMBERSHIP_RESOURCES = [
  "groups",
  "employees",
  "representatives",
  "external-contacts",
] as const satisfies readonly CentralResource[];
